import { useRef, useState } from "react";
import type { PayoutView } from "@/services/types";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Textarea } from "@/components/base/Textarea";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatINRNumber } from "@/utils/format";

interface ApproveModalProps {
  payout: PayoutView;
  onClose: () => void;
  onConfirm: (remarks: string) => Promise<boolean>;
}

/**
 * Approval is a distinct, deliberate step from disbursement — the person
 * authorising the money and the person handing it over are often different.
 * Shows the full breakdown and records who approved and when.
 */
export function ApproveModal({ payout, onClose, onConfirm }: ApproveModalProps) {
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleConfirm = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const ok = await onConfirm(remarks);
      if (ok) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not approve this payout.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Approve this payout?"
      description="Approval authorises the money to be paid. Disbursement is a separate step."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" icon={<i className="ri-check-double-line" />} loading={submitting} onClick={handleConfirm}>
            Approve payout
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md bg-info-100 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-info-800">Net payable</p>
          <p className="tnum text-2xl font-bold text-foreground-950"><CurrencyDisplay amount={payout.netPayable} hideSymbol /></p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="col-span-2 flex items-center justify-between border-t border-background-100 pt-2">
            <dt className="text-foreground-600">Prize</dt>
            <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={payout.prize} hideSymbol /></dd>
          </div>
          <div className="col-span-2 flex items-center justify-between">
            <dt className="text-foreground-600">Same-group dues adjusted</dt>
            <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={payout.sameGroupDuesTotal} hideSymbol /></dd>
          </div>
          {payout.manualDeductionsTotal > 0 ? (
            <div className="col-span-2 flex items-center justify-between">
              <dt className="text-foreground-600">Other deductions</dt>
              <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={payout.manualDeductionsTotal} hideSymbol /></dd>
            </div>
          ) : null}
          <div className="col-span-2 flex items-center justify-between border-t border-background-100 pt-2">
            <dt className="text-foreground-600">Net payable</dt>
            <dd className="tnum text-base font-bold text-foreground-950"><CurrencyDisplay amount={payout.netPayable} hideSymbol /></dd>
          </div>
        </dl>
        <p className="text-xs text-foreground-500">
          Approving {payout.winnerName} ({payout.winnerTicketNumber}) receives {formatINRNumber(payout.netPayable)} for{" "}
          {payout.groupName}, cycle {payout.cycle}. This will be recorded as approved with your name and timestamp.
        </p>
        <Textarea
          label="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Optional note about this approval…"
          rows={3}
          maxLength={500}
        />
        {error ? <p className="text-xs text-danger-600">{error}</p> : null}
      </div>
    </Modal>
  );
}